import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import { InfoList } from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import DopsAssessmentForm from './DopsAssessmentForm'
import {
  DopsStatusChip,
  OVERALL_COLORS,
  apiError,
  formatDate,
  personName,
  snapshotRows,
} from './dopsShared'

const RATING_COLORS = {
  'Below expectations': 'error',
  Borderline: 'warning',
  'Meets expectations': 'success',
  'Above expectations': 'success',
  'Not observed': 'default',
}

const Section = ({ title, caption, children, action }) => (
  <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
      <Box minWidth={0}>
        <Typography fontWeight={800}>{title}</Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
    {children}
  </Card>
)

const TextBlock = ({ label, text }) =>
  text ? (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    </Box>
  ) : null

// Feedback the assessor gave (read-only)
const Outcome = ({ dops }) => (
  <Stack gap={1.5}>
    {dops.overall && (
      <Chip
        label={dops.overall}
        color={OVERALL_COLORS[dops.overall] ?? 'default'}
        sx={{ alignSelf: 'flex-start', borderRadius: 1, fontWeight: 700 }}
      />
    )}
    {(dops.ratings ?? []).length > 0 && (
      <Stack divider={<Divider />} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
        {dops.ratings.map((row) => (
          <Stack
            key={row.domain}
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={0.5}
            px={1.5}
            py={0.75}
          >
            <Typography variant="body2">{row.domain}</Typography>
            <Chip
              size="small"
              variant="outlined"
              label={row.rating}
              color={RATING_COLORS[row.rating] ?? 'default'}
              sx={{ borderRadius: 1, flexShrink: 0 }}
            />
          </Stack>
        ))}
      </Stack>
    )}
    <TextBlock label="Strengths" text={dops.strengths} />
    <TextBlock label="Development points" text={dops.development} />
    <TextBlock label="Agreed actions" text={dops.agreedActions} />
  </Stack>
)

const DopsDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [dops, setDops] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')
  const [reflection, setReflection] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState('')

  const load = useCallback(() => {
    API.get(`/dops/${id}`)
      .then((res) => {
        if (res.status === 200) {
          setDops(res.data)
          setReflection(res.data?.traineeReflection ?? '')
          setFetchError('')
        } else {
          setFetchError(apiError(res, 'Unable to load this DOPS'))
        }
      })
      .catch(() => setFetchError('Unable to load this DOPS'))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  const saveReflection = () => {
    setBusy(true)
    setActionError('')
    API.post(`/dops/${id}/reflection`, { text: reflection })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Reflection saved' })
          load()
        } else setActionError(apiError(res, 'Unable to save your reflection'))
      })
      .catch(() => setActionError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  const withdraw = () => {
    if (!window.confirm('Withdraw this DOPS request? The assessor will no longer see it.')) return
    setBusy(true)
    setActionError('')
    API.post(`/dops/${id}/withdraw`)
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'DOPS request withdrawn' })
          load()
        } else setActionError(apiError(res, 'Unable to withdraw this request'))
      })
      .catch(() => setActionError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  if (loading) return <LinearProgress />

  const back = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(dops?.viewerRole === 'governance' ? '/governance/dops' : '/dops')}
      sx={{ alignSelf: 'flex-start' }}
    >
      {dops?.viewerRole === 'governance' ? 'All DOPS' : 'DOPS & logbook'}
    </Button>
  )

  if (fetchError || !dops) {
    return (
      <Stack gap={1.5} sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
        {back}
        <Alert severity="error">{fetchError || 'DOPS not found'}</Alert>
      </Stack>
    )
  }

  const role = dops.viewerRole
  const rows = snapshotRows(dops.snapshot)

  return (
    <Stack gap={2} sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
      {back}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
        <Box minWidth={0}>
          <Typography variant="title2" component="div">
            DOPS: {dops.procedureLabel || 'Procedure'}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {personName(dops.trainee)} assessed by {personName(dops.assessor)}
            {dops.performedAt ? ` · performed ${formatDate(dops.performedAt, 'DD/MM/YYYY HH:mm')}` : ''}
            {dops.epcrNumber ? ` · ePCR ${dops.epcrNumber}` : ''}
          </Typography>
        </Box>
        <DopsStatusChip status={dops.status} size="medium" />
      </Stack>

      {role === 'assessor' && dops.status === 'Requested' && (
        <Alert severity="info">
          {personName(dops.trainee)} has asked you to assess this procedure.
          Review the intervention details below, then rate each domain.
        </Alert>
      )}

      {dops.requestMessage && (
        <Section title="Request message" caption={`From ${personName(dops.trainee)}`}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {dops.requestMessage}
          </Typography>
        </Section>
      )}

      <Section title="Intervention" caption="As recorded on the ePCR (no patient identifiers)">
        {rows.length ? (
          <InfoList rows={rows} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No details were recorded for this intervention.
          </Typography>
        )}
      </Section>

      {role === 'assessor' && dops.status === 'Requested' && (
        <Section title="Assessment">
          <DopsAssessmentForm
            dops={dops}
            onSaved={(verb) => {
              setToast({ message: `DOPS ${verb}` })
              load()
            }}
          />
        </Section>
      )}

      {dops.status === 'Completed' && (
        <Section
          title="Assessor feedback"
          caption={`Completed ${formatDate(dops.completedAt, 'DD/MM/YYYY HH:mm')} by ${personName(dops.assessor)}`}
        >
          <Outcome dops={dops} />
        </Section>
      )}

      {dops.status === 'Declined' && (
        <Alert severity="warning">
          {personName(dops.assessor)} declined this DOPS
          {dops.declineReason ? `: ${dops.declineReason}` : '.'}
        </Alert>
      )}
      {dops.status === 'Withdrawn' && (
        <Alert severity="info">This DOPS request was withdrawn.</Alert>
      )}
      {role === 'trainee' && dops.status === 'Requested' && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={withdraw} disabled={busy}>
              Withdraw
            </Button>
          }
        >
          Waiting for {personName(dops.assessor)} to complete the assessment.
        </Alert>
      )}

      {dops.status === 'Completed' &&
        (role === 'trainee' ? (
          <Section
            title="Your reflection"
            caption={
              dops.reflectedAt
                ? `Last saved ${formatDate(dops.reflectedAt, 'DD/MM/YYYY HH:mm')}`
                : 'What did you learn, and what will you do differently? This forms part of your CPD evidence.'
            }
          >
            <Stack gap={1.5}>
              <TextField
                multiline
                minRows={4}
                fullWidth
                size="small"
                placeholder="Write your reflection"
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={saveReflection}
                disabled={busy || !reflection.trim() || reflection.trim() === (dops.traineeReflection ?? '')}
                startIcon={busy ? <CircularProgress size={16} /> : null}
                sx={{ alignSelf: 'flex-end' }}
              >
                Save reflection
              </Button>
            </Stack>
          </Section>
        ) : dops.traineeReflection ? (
          <Section
            title="Trainee reflection"
            caption={dops.reflectedAt ? formatDate(dops.reflectedAt, 'DD/MM/YYYY HH:mm') : undefined}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {dops.traineeReflection}
            </Typography>
          </Section>
        ) : null)}

      {actionError && <Alert severity="error">{actionError}</Alert>}
    </Stack>
  )
}

export default DopsDetail
